import React, { useRef, useLayoutEffect } from 'react'

import { useTable, usePagination } from 'react-table'

import { Th, Td, Table, Tr, Tbody } from '../index'
import Pagination from '../Pagination/Pagination'

export interface Props {
  data: Record<string, unknown>[]
  columns: Record<string, unknown>[]
  pageCount?: number
  fetchData?: Function
  columnsSize?: string
  rowsCount?: number
  pagination?: {
    mainLabel?: string
  }
}

const DataGrid = ({
  data,
  columns,
  pageCount,
  fetchData,
  columnsSize = `repeat(${columns.length}, 1fr)`,
  rowsCount = 10,
  pagination,
}: Props): React.ReactElement => {
  const firstUpdate = useRef<boolean>(true)

  const reactTable = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: rowsCount },
      manualPagination: true,
      pageCount,
    },
    usePagination
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state: { pageIndex, pageSize },
  } = reactTable

  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }

    fetchData({ pageSize, pageIndex })
  }, [fetchData, pageIndex, pageSize])

  return (
    <div className="flex flex-col gap-y-2">
      <Table className="mt-5" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <Tr
              columnsSize={columnsSize}
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map((column) => (
                <Th {...column.getHeaderProps()}>{column.render('Header')}</Th>
              ))}
            </Tr>
          ))}
        </thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row)
            return (
              <Tr
                columnsSize={columnsSize}
                index={index}
                {...row.getRowProps()}
              >
                {row.cells.map((cell) => (
                  <Td {...cell.getCellProps()}>{cell.render('Cell')}</Td>
                ))}
              </Tr>
            )
          })}
        </Tbody>
      </Table>
      {pagination && <Pagination {...reactTable} pagination={pagination} />}
    </div>
  )
}

export default DataGrid
